import { Button, InputField, Popup } from "components";
import { ChangeEvent, useEffect, useId, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  createWhitelist,
  getWhitelist,
  removeWhitelistItem,
} from "store/slices/client.slice";
import { getCompanyPixel } from "store/slices/company.slice";
import { RootState } from "store/store";
import EntryFormatter from "utils/EntryFormatter";
import { PixelImplementationStep } from "../PixelImplementationStep";
import { ClientWhitelistDataTypeENUM } from "utils/enums/clientENUM";
import { toastHelper } from "utils/helper";
import {
  errorMessages,
  validationMessages,
  successMessages,
} from "utils/message";
import { Card } from "components/atoms/Card";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { isURL } from "validator";
import CompanyPixel from "../CompanyPixel/CompanyPixel";

export const PixelWhitelist = () => {
  const dispatch = useAppDispatch();
  const [showConfirmModal, setShowConfirmModal] = useState("");
  const { data: whitelist } = useAppSelector(
    (state: RootState) => state.client.whitelist
  );
  const defaultToastId = useId();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(getCompanyPixel()),
          dispatch(getWhitelist()),
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data. Please try again later.");
      } finally {
      }
    };

    fetchData();
  }, [dispatch]);
  const handleAddUrl = async ({ url: newUrl }: { url: string }) => {
    if (validateEmptyField(newUrl)) {
      form.setError("url", {
        type: "manual",
        message: validationMessages.EMPTY_FIELD,
      });
      return;
    }
    if (validateDomain(newUrl)) {
      form.setError("url", {
        type: "manual",
        message: validationMessages.VALID_DOMAIN,
      });
      return;
    }
    if (!validateMaxLength(newUrl)) {
      form.setError("url", {
        type: "manual",
        message: validationMessages.URL_TOO_LONG,
      });
      return;
    }

    const treatValue =
      newUrl.startsWith("http://") || newUrl.startsWith("https://")
        ? newUrl
        : `https://${newUrl}`;
    const hasWhitelist = whitelist?.find((item) => {
      const removedHttpOrHttps = treatValue.split("/");
      return item.dataValue === removedHttpOrHttps[2];
    });
    if (hasWhitelist) {
      form.setError("url", {
        type: "manual",
        message: validationMessages.URL_ALREADY_EXISTS,
      });
      return;
    }

    try {
      await dispatch(
        createWhitelist({
          dataType: ClientWhitelistDataTypeENUM.DOMAIN,
          dataValue: treatValue,
        })
      );
      form.reset();
      toastHelper({
        type: "success",
        toastId: defaultToastId,
        message: successMessages.ADD_SUCCESS,
      });
    } catch (error) {
      toastHelper({
        type: "error",
        toastId: defaultToastId,
        message: errorMessages.SOMETHING_WENT_WRONG,
      });
    }
  };
  const handleDeleteUrl = async () => {
    toastHelper({
      type: "info",
      toastId: defaultToastId,
      message: "Removing URL...",
    });
    try {
      await dispatch(removeWhitelistItem(showConfirmModal));
      toastHelper({
        type: "success",
        toastId: defaultToastId,
        message: successMessages.URL_REMOVED_SUCCESS,
      });
      handleCancelDelete();
    } catch (error) {
      toastHelper({
        type: "error",
        toastId: defaultToastId,
        message: errorMessages.FAILED_REMOVE_URL,
      });
    }
  };
  const handleCancelDelete = () => {
    setShowConfirmModal("");
  };
  const handleShowModalDeleteUrl = (id: string) => {
    setShowConfirmModal(id);
  };
  const validateEmptyField = (value: string) => !value.trim();
  const validateDomain = (value: string): boolean =>
    !EntryFormatter.isDomain(value);
  const validateMaxLength = (value: string): boolean => {
    if (value.length > 512) {
      form.setError("url", { type: "manual", message: "URL is too long" });
      return false;
    }
    return true;
  };
  const form = useForm({
    defaultValues: {
      url: "",
    },
  });
  return (
    <Card
      hiddenHeader
      bodyClassName="h-full gap-4 flex flex-col justify-between"
      className="h-full"
    >
      <CompanyPixel />
      <div className="flex flex-col flex-1 gap-1">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleAddUrl)}>
            <div className="flex gap-3 mb-4">
              <Controller
                name="url"
                render={({ field }) => {
                  return (
                    <InputField
                      name="url"
                      size="tiny"
                      value={field.value}
                      onChange={field.onChange}
                      errors={form.formState.errors}
                      placeholder="Enter URL (e.g., https://example.com)"
                      width="full"
                    />
                  );
                }}
              />
              <Button
                size="xs"
                iconName="plus"
                action="submit"
                className="max-h-[48px]"
                disabled={!form.watch("url")}
              >
                Add URL
              </Button>
            </div>
          </form>
        </FormProvider>
        {whitelist && whitelist.length > 0 ? (
          <div className="gap-4 flex flex-row items-center flex-wrap">
            {whitelist.map((listItem) => (
              <div
                onClick={() => handleShowModalDeleteUrl(listItem.id)}
                key={listItem.id}
                className="flex items-center justify-between bg-gray-900 rounded-md"
              >
                <span className="text-white-500 badge pm">
                  {listItem.dataValue}
                  <Button size="xxs"> x </Button>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No URLs added yet</p>
        )}
        <br />
        {showConfirmModal && (
          <Popup noButton closeOnOutsideClick={false} onClose={handleDeleteUrl}>
            <div className="flex items-center justify-center z-50">
              <div className="p-6 rounded-lg">
                <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
                <p className="mb-4">
                  Are you sure you want to delete this page URL?
                </p>
                <div className="flex justify-end">
                  <Button
                    className="bg-red-500 text-white-500 px-4 py-2 rounded mr-2"
                    onClick={handleDeleteUrl}
                  >
                    Yes
                  </Button>
                  <Button
                    className="bg-gray-400 text-white-500 px-4 py-2 rounded"
                    onClick={handleCancelDelete}
                  >
                    No
                  </Button>
                </div>
              </div>
            </div>
          </Popup>
        )}
      </div>
    </Card>
  );
};